import DOMPurify from "isomorphic-dompurify";

/**
 * Sanitize plain text input (strips all HTML tags)
 * Use for: names, titles, addresses, single-line inputs
 */
export function sanitizeText(text: string): string {
  if (!text) return "";
  return DOMPurify.sanitize(text, {
    ALLOWED_TAGS: [], // No tags allowed
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true, // Keep text content
  });
}

/**
 * Sanitize HTML content (allows safe formatting tags)
 * Use for: descriptions, rich text content, multi-line inputs
 */
export function sanitizeHtml(html: string): string {
  if (!html) return "";
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "b",
      "i",
      "em",
      "strong",
      "p",
      "br",
      "ul",
      "ol",
      "li",
      "a",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
    ],
    ALLOWED_ATTR: ["href", "target", "rel"],
    ALLOW_DATA_ATTR: false,
  });
}

/**
 * Sanitize URL (ensures it's a safe URL)
 * Use for: links, images, external references
 */
export function sanitizeUrl(url: string): string {
  if (!url) return "";

  const sanitized = DOMPurify.sanitize(url, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });

  // Only allow http, https, and mailto protocols
  try {
    const urlObj = new URL(sanitized);
    if (["http:", "https:", "mailto:"].includes(urlObj.protocol)) {
      return sanitized;
    }
  } catch {
    // Invalid URL
  }

  return "";
}

/**
 * Sanitize property data before saving to database
 */
export function sanitizePropertyData(data: any): any {
  return {
    ...data,
    title: sanitizeText(data.title),
    address: sanitizeText(data.address),
    description: data.description ? sanitizeHtml(data.description) : undefined,
    amenities: Array.isArray(data.amenities)
      ? data.amenities.map((a: string) => sanitizeText(a))
      : [],
    nearbyPlaces: Array.isArray(data.nearbyPlaces)
      ? data.nearbyPlaces.map((p: string) => sanitizeText(p))
      : [],
  };
}

/**
 * Sanitize maintenance request data
 */
export function sanitizeMaintenanceData(data: any): any {
  return {
    ...data,
    title: sanitizeText(data.title),
    description: sanitizeHtml(data.description),
    category: sanitizeText(data.category),
    location: data.location ? sanitizeText(data.location) : undefined,
  };
}

/**
 * Sanitize user profile data
 */
export function sanitizeUserData(data: any): any {
  return {
    ...data,
    name: sanitizeText(data.name),
    email: sanitizeText(data.email),
    phone: data.phone ? sanitizeText(data.phone) : undefined,
    bio: data.bio ? sanitizeHtml(data.bio) : undefined,
  };
}

/**
 * Sanitize lease/application data
 */
export function sanitizeLeaseData(data: any): any {
  return {
    ...data,
    tenantName: sanitizeText(data.tenantName),
    landlordName: data.landlordName
      ? sanitizeText(data.landlordName)
      : undefined,
    propertyTitle: data.propertyTitle
      ? sanitizeText(data.propertyTitle)
      : undefined,
    notes: data.notes ? sanitizeHtml(data.notes) : undefined,
    terminationReason: data.terminationReason
      ? sanitizeHtml(data.terminationReason)
      : undefined,
  };
}
